import { ReactNode } from 'react';

import { GetStartupData_startupData_years } from 'approot/shared/graphql/startup-data/__generated__/GetStartupData';
import { SubjectFragment } from 'approot/shared/graphql/startup-data/__generated__/SubjectFragment';
import { OutcomesFragment } from 'approot/shared/graphql/outcomes/__generated__/OutcomesFragment';
import { InquisitiveEnglishContentArea } from 'global/graphql/scalars.type';
import { useYearTranslation } from 'approot/shared/logic/years';
import { useAccessibleHeading } from 'approot/shared/ally/accessible-heading';
import ContentPageBreadcrumbs from './content-page-breadcrumbs';
import ContentPageHeaderText from './content-page-header-text';
import * as Styled from './content-page.style';
import { ActionButtonWrapper } from 'approot/shared/action-button/action-button';
import {
  useImageLoad,
  useResponsiveImage,
} from 'approot/shared/images/images.hooks';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import { grey1 } from 'approot/shared/styles/colours.style';

type ContentItem = {
  name: string;
  slug: string;
};

type Props = {
  title: string;
  years: GetStartupData_startupData_years[];
  navigation: ReactNode;
  subject: SubjectFragment;
  breadcrumbSubject?: SubjectFragment;
  topic: ContentItem;
  unit?: ContentItem;
  item?: ContentItem;
  englishContentArea?: InquisitiveEnglishContentArea[] | null;
  hideBreadcrumbs?: boolean;
  performanceExpectations?: OutcomesFragment[] | null;
  badge?: ReactNode;
  archived?: boolean;
};

export const ContentPageHeader = ({
  title,
  years,
  navigation,
  subject,
  breadcrumbSubject,
  topic,
  unit,
  item,
  englishContentArea,
  hideBreadcrumbs,
  performanceExpectations,
  badge,
  archived,
}: Props) => {
  const { registerHeading } = useAccessibleHeading();
  const { formatYears } = useYearTranslation();

  return (
    <>
      {!hideBreadcrumbs && (
        <ContentPageBreadcrumbs
          subject={breadcrumbSubject || subject}
          years={years}
          unit={unit}
          topic={topic}
          item={item}
        />
      )}
      <Styled.ContentPageMastheadTitle
        ref={registerHeading}
        tabIndex={-1}
        aria-label={`${title}, ${subject.label}, ${formatYears(
          years.map(y => y.name)
        )}`}
        data-selector="content-title"
      >
        {title}
      </Styled.ContentPageMastheadTitle>
      <Styled.ContentPageMastheadDetails>
        <ContentPageHeaderText
          englishContentArea={englishContentArea}
          performanceExpectations={performanceExpectations}
        />
        {badge}
        {navigation}
      </Styled.ContentPageMastheadDetails>
    </>
  );
};

export const ContentPageHeaderImage = ({
  isEditMode,
  images,
}: {
  isEditMode: boolean;
  images?: ResponsiveImage[] | null;
}) => {
  const imageUrl = useResponsiveImage(images || [], 780);
  const { imageHasLoaded } = useImageLoad(imageUrl || '');
  return (
    <Styled.ContentPageHeaderImageWrapper>
      <Styled.ContentPageHeaderImage
        imageUrl={imageUrl}
        imageHasLoaded={imageHasLoaded}
      />
      {isEditMode && (
        <ActionButtonWrapper
          buttons={[
            {
              title: 'Edit',
              onClick: () => {
                document.getElementById('hero-edit')?.scrollIntoView({
                  behavior: 'smooth',
                });
              },
              ariaLabel: 'Edit the landscape hero image',
              backgroundColour: grey1,
            },
            {
              title: 'Replace',
              onClick: () => {
                document.getElementById('hero-edit')?.scrollIntoView({
                  behavior: 'smooth',
                });
              },
              ariaLabel: 'Replace the landscape hero image',
              backgroundColour: grey1,
            },
          ]}
        />
      )}
    </Styled.ContentPageHeaderImageWrapper>
  );
};
